<template>
  <div>
    <v-toolbar flat color="white">
      <v-toolbar-title>Envanter Tanımları</v-toolbar-title>
      <v-spacer></v-spacer>
      <v-btn color="primary" dark @click="openCreate">
        <v-icon left>add</v-icon>
        Yeni Tanım
      </v-btn>
    </v-toolbar>

    <div class="caption grey--text px-3 pb-2">
      Envantere kalem eklerken çıkan açılır listeleri buradan yönetin. Takı için ürün/ölçü/ayar, Külçe için ürün/ayar,
      Ziynet için ürün tanımlanır. Pasif tanımlar ekleme ekranlarında listelenmez.
    </div>

    <div class="px-3 pb-2 d-flex align-center" style="gap: 12px; flex-wrap: wrap;">
      <v-select
        v-model="selectedType"
        :items="typeItems"
        item-text="label"
        item-value="value"
        label="Tip"
        hide-details
        style="max-width: 180px"
        @change="onTypeChange"
      ></v-select>
      <v-btn-toggle v-model="selectedCategory" mandatory class="elevation-0">
        <v-btn
          v-for="c in availableCategories"
          :key="'cat-' + c.value"
          :value="c.value"
          flat
        >{{ c.label }}</v-btn>
      </v-btn-toggle>
    </div>

    <v-data-table
      :headers="headers"
      :items="items"
      :loading="loading"
      :rows-per-page-items="[50, 100, { text: 'Tümü', value: -1 }]"
      class="elevation-1"
    >
      <template v-slot:items="props">
        <td class="px-2">{{ props.item.label }}</td>
        <td class="px-2 text-xs-right">{{ props.item.value != null ? formatNum(props.item.value) : '—' }}</td>
        <td v-if="isZiynetProduct" class="px-2">
          {{ props.item.purity != null ? formatNum(props.item.purity) : '—' }}
          <span v-if="props.item.purityLabel" class="grey--text">({{ props.item.purityLabel }})</span>
        </td>
        <td class="px-2">
          <v-chip small :color="props.item.isActive ? 'green' : 'grey'" text-color="white">
            {{ props.item.isActive ? 'Aktif' : 'Pasif' }}
          </v-chip>
        </td>
        <td class="px-2">
          <v-icon small class="mr-2" color="blue" @click="openEdit(props.item)">edit</v-icon>
          <v-icon small color="red" @click="remove(props.item)">delete</v-icon>
        </td>
      </template>
      <template v-slot:no-data>
        <div class="text-xs-center pa-4 grey--text">Bu liste için henüz tanım yok.</div>
      </template>
    </v-data-table>

    <v-dialog v-model="dialog" max-width="460" persistent>
      <v-card>
        <v-card-title class="headline primary white--text">
          {{ editing ? 'Tanım Düzenle' : 'Yeni Tanım' }} — {{ currentTypeLabel }} / {{ currentCategoryLabel }}
        </v-card-title>
        <v-card-text class="pa-4">
          <v-text-field v-model="form.label" :label="labelFieldLabel" :placeholder="labelPlaceholder" autofocus></v-text-field>
          <v-text-field
            v-if="showValue"
            v-model="form.value"
            :label="valueFieldLabel"
            :placeholder="valuePlaceholder"
          ></v-text-field>
          <v-text-field
            v-if="isZiynetProduct"
            v-model="form.purity"
            label="Ayar / Milyem (ör. 916,6)"
            placeholder="916,6"
          ></v-text-field>
          <v-text-field
            v-if="isZiynetProduct"
            v-model="form.purityLabel"
            label="Ayar Etiketi (ör. 22 Ayar)"
            placeholder="22 Ayar"
          ></v-text-field>
          <v-text-field v-model.number="form.sortOrder" label="Sıra" type="number"></v-text-field>
          <v-switch v-model="form.isActive" :label="form.isActive ? 'Aktif' : 'Pasif'" color="green"></v-switch>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn flat @click="dialog = false">İptal</v-btn>
          <v-btn color="primary" dark :loading="saving" @click="save">Kaydet</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
// Sabitler backend ile aynı: InventoryType 0=Takı,1=Ziynet,2=Külçe; Category 0=Ürün,1=Ölçü,2=Ayar
const CAT = { Product: 0, Size: 1, Ayar: 2 };

export default {
  name: "InventoryDefinitions",
  data: () => ({
    loading: false,
    saving: false,
    items: [],
    selectedType: 0,
    selectedCategory: 0,
    dialog: false,
    editing: false,
    form: { id: null, label: "", value: "", purity: "", purityLabel: "", sortOrder: 0, isActive: true },
    typeItems: [
      { value: 0, label: "Takı" },
      { value: 2, label: "Külçe" },
      { value: 1, label: "Ziynet" },
    ],
    categoriesByType: {
      0: [
        { value: CAT.Product, label: "Ürün" },
        { value: CAT.Size, label: "Ölçü" },
        { value: CAT.Ayar, label: "Ayar" },
      ],
      2: [
        { value: CAT.Product, label: "Ürün" },
        { value: CAT.Ayar, label: "Ayar" },
      ],
      1: [{ value: CAT.Product, label: "Ürün" }],
    },
  }),

  computed: {
    availableCategories() {
      return this.categoriesByType[this.selectedType] || [];
    },
    currentTypeLabel() {
      const t = this.typeItems.find((x) => x.value === this.selectedType);
      return t ? t.label : "";
    },
    currentCategoryLabel() {
      const c = this.availableCategories.find((x) => x.value === this.selectedCategory);
      return c ? c.label : "";
    },
    isAyar() {
      return this.selectedCategory === CAT.Ayar;
    },
    isZiynetProduct() {
      return this.selectedType === 1 && this.selectedCategory === CAT.Product;
    },
    isKulceProduct() {
      return this.selectedType === 2 && this.selectedCategory === CAT.Product;
    },
    showValue() {
      return this.isAyar || this.isKulceProduct || this.isZiynetProduct;
    },
    labelFieldLabel() {
      if (this.selectedCategory === CAT.Size) return "Ölçü";
      if (this.isAyar) return "Ayar Adı";
      return "Ürün Adı";
    },
    labelPlaceholder() {
      if (this.selectedCategory === CAT.Size) return "Örn. 7.2";
      if (this.isAyar) return "Örn. 22 Ayar";
      if (this.isKulceProduct) return "Örn. 1 gr";
      if (this.isZiynetProduct) return "Örn. Çeyrek";
      return "Örn. Bilezik";
    },
    valueFieldLabel() {
      if (this.isAyar) return "Milyem (ör. 916,6)";
      return "Gram (ör. 1,75)";
    },
    valuePlaceholder() {
      return this.isAyar ? "916,6" : "1,75";
    },
    headers() {
      const h = [
        { text: this.labelFieldLabel, value: "label" },
        { text: this.isAyar ? "Milyem" : "Değer", value: "value", align: "right" },
      ];
      if (this.isZiynetProduct) h.push({ text: "Ayar", value: "purity" });
      h.push({ text: "Durum", value: "isActive" });
      h.push({ text: "", value: "actions", sortable: false });
      return h;
    },
  },

  watch: {
    selectedCategory() {
      this.load();
    },
  },

  created() {
    this.load();
  },

  methods: {
    parseNum(s) {
      if (s == null || s === "") return null;
      const n = parseFloat(String(s).trim().replace(",", "."));
      return Number.isNaN(n) ? null : n;
    },
    formatNum(v) {
      if (v == null) return "—";
      const n = Number(v);
      if (Number.isNaN(n)) return String(v);
      return n.toLocaleString("tr-TR", { maximumFractionDigits: 4 });
    },
    trStr(n) {
      return n == null ? "" : String(n).replace(".", ",");
    },
    onTypeChange() {
      // Tip değişince mevcut kategori geçersizse ilkine düş (watch -> load tetikler).
      // Geçerliyse kategori değişmez; load'u elle çağırırız.
      if (!this.availableCategories.some((c) => c.value === this.selectedCategory)) {
        this.selectedCategory = this.availableCategories[0].value;
      } else {
        this.load();
      }
    },
    async load() {
      this.loading = true;
      try {
        this.items =
          (await this.$store.dispatch("inventory/getDefinitions", {
            inventoryType: this.selectedType,
            category: this.selectedCategory,
          })) || [];
      } catch (e) {
        this.notify("Tanımlar yüklenemedi: " + this.errMsg(e));
      } finally {
        this.loading = false;
      }
    },
    openCreate() {
      this.editing = false;
      const nextSort = this.items.length ? Math.max(...this.items.map((i) => i.sortOrder || 0)) + 1 : 1;
      this.form = { id: null, label: "", value: "", purity: "", purityLabel: "", sortOrder: nextSort, isActive: true };
      this.dialog = true;
    },
    openEdit(item) {
      this.editing = true;
      this.form = {
        id: item.id,
        label: item.label,
        value: this.trStr(item.value),
        purity: this.trStr(item.purity),
        purityLabel: item.purityLabel || "",
        sortOrder: item.sortOrder || 0,
        isActive: item.isActive,
      };
      this.dialog = true;
    },
    buildDto() {
      return {
        inventoryType: this.selectedType,
        category: this.selectedCategory,
        label: (this.form.label || "").trim(),
        value: this.showValue ? this.parseNum(this.form.value) : null,
        purity: this.isZiynetProduct ? this.parseNum(this.form.purity) : null,
        purityLabel: this.isZiynetProduct ? (this.form.purityLabel || "").trim() : null,
        sortOrder: Number(this.form.sortOrder) || 0,
        isActive: this.form.isActive,
      };
    },
    async save() {
      if (!this.form.label || !this.form.label.trim()) {
        this.notify("Ad/etiket girin.");
        return;
      }
      this.saving = true;
      try {
        const dto = this.buildDto();
        if (this.editing) {
          await this.$store.dispatch("inventory/updateDefinition", { id: this.form.id, dto });
        } else {
          await this.$store.dispatch("inventory/createDefinition", dto);
        }
        this.dialog = false;
        await this.load();
      } catch (e) {
        this.notify("Hata: " + this.errMsg(e));
      } finally {
        this.saving = false;
      }
    },
    async remove(item) {
      if (!confirm(`'${item.label}' silinsin mi?`)) return;
      try {
        await this.$store.dispatch("inventory/deleteDefinition", item.id);
        await this.load();
      } catch (e) {
        this.notify("Hata: " + this.errMsg(e));
      }
    },
    errMsg(e) {
      return (e && e.response && e.response.data && e.response.data.message) || (e && e.message) || "Bilinmeyen hata";
    },
    notify(msg) {
      this.$store.state.dialog = true;
      this.$store.state.message = msg;
    },
  },
};
</script>
